import {FC, useRef, useState} from 'react'
import {api, ServiceCategory} from '../../services/api'
import {usePaginated} from '../../hooks/usePaginated'
import {Pagination} from '../../components/Pagination'
import {toast} from '../../utils/toast'

// ── Image Picker ──────────────────────────────────────────────────────────────
// Shows the current/selected image with a "Remove" affordance. Service categories
// are image-driven here — no icon is shown or edited in the admin panel; the
// mobile app falls back to a stored icon only when no image exists.
const ImagePicker: FC<{
  preview: string | null
  onSelect: (file: File) => void
  onRemove: () => void
}> = ({preview, onSelect, onRemove}) => {
  const inputRef = useRef<HTMLInputElement>(null)

  return (
    <div>
      <input ref={inputRef} type='file' accept='image/*' style={{display: 'none'}}
        onChange={e => { const f = e.target.files?.[0]; if (f) onSelect(f); e.target.value = '' }} />

      {preview ? (
        <div style={{position: 'relative', width: 140, height: 140, borderRadius: 12, overflow: 'hidden', border: '1px solid #e2e8f0'}}>
          <img src={preview} alt='cover' style={{width: '100%', height: '100%', objectFit: 'cover'}} />
          <div style={{position: 'absolute', bottom: 0, left: 0, right: 0, display: 'flex'}}>
            <button type='button' onClick={() => inputRef.current?.click()}
              style={{flex: 1, padding: '7px 0', border: 'none', background: 'rgba(15,23,42,0.78)', color: '#fff', fontSize: 12, fontWeight: 600, cursor: 'pointer'}}>
              Change
            </button>
            <button type='button' onClick={onRemove}
              style={{flex: 1, padding: '7px 0', border: 'none', background: 'rgba(220,38,38,0.85)', color: '#fff', fontSize: 12, fontWeight: 600, cursor: 'pointer'}}>
              Remove
            </button>
          </div>
        </div>
      ) : (
        <div onClick={() => inputRef.current?.click()}
          style={{
            width: 140, height: 140, borderRadius: 12, cursor: 'pointer',
            border: '2px dashed #cbd5e1', background: '#f8fafc',
            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 8,
          }}>
          <svg width='28' height='28' viewBox='0 0 24 24' fill='none' stroke='#94a3b8' strokeWidth='2'>
            <rect x='3' y='3' width='18' height='18' rx='2' ry='2'/><circle cx='8.5' cy='8.5' r='1.5'/><polyline points='21 15 16 10 5 21'/>
          </svg>
          <span style={{fontSize: 12, color: '#94a3b8', fontWeight: 500}}>Upload image</span>
        </div>
      )}
    </div>
  )
}

// ── Toggle Switch ─────────────────────────────────────────────────────────────
const Toggle: FC<{checked: boolean; onChange: () => void}> = ({checked, onChange}) => (
  <div onClick={onChange} style={{
    width: 40, height: 22, borderRadius: 11, cursor: 'pointer', flexShrink: 0,
    background: checked ? '#22c55e' : '#e2e8f0',
    position: 'relative', transition: 'background 0.2s',
  }}>
    <div style={{
      position: 'absolute', top: 3, left: checked ? 21 : 3,
      width: 16, height: 16, borderRadius: '50%', background: '#fff',
      boxShadow: '0 1px 3px rgba(0,0,0,0.2)', transition: 'left 0.2s',
    }} />
  </div>
)

// ── Main Page ─────────────────────────────────────────────────────────────────
const ServiceCategoriesPage: FC = () => {
  const {items: categories, total, page, setPage, search, setSearch, limit, loading, error, reload} =
    usePaginated((p, l, s) => api.getServiceCategoriesPaged(p, l, s), {limit: 20})

  const [showModal, setShowModal]       = useState(false)
  const [editTarget, setEditTarget]     = useState<ServiceCategory | null>(null)
  const [deleteTarget, setDeleteTarget] = useState<ServiceCategory | null>(null)
  const [saving, setSaving]             = useState(false)
  const [actionError, setActionError]   = useState('')

  const [formLabel, setFormLabel]       = useState('')
  const [formOrder, setFormOrder]       = useState('')
  const [formImageFile, setFormImageFile] = useState<File | null>(null)
  const [formPreview, setFormPreview]   = useState<string | null>(null)
  const [formRemoveImage, setFormRemoveImage] = useState(false)
  const [formActive, setFormActive]     = useState(true)
  const [formError, setFormError]       = useState('')

  const openAdd = () => {
    setEditTarget(null)
    setFormLabel(''); setFormOrder(''); setFormImageFile(null); setFormPreview(null); setFormRemoveImage(false); setFormActive(true); setFormError('')
    setShowModal(true)
  }

  const openEdit = (c: ServiceCategory) => {
    setEditTarget(c)
    setFormLabel(c.label); setFormOrder(String(c.order))
    setFormImageFile(null); setFormPreview(c.image ?? null); setFormRemoveImage(false); setFormActive(c.isActive); setFormError('')
    setShowModal(true)
  }

  const handleSelectImage = (file: File) => {
    setFormImageFile(file)
    setFormPreview(URL.createObjectURL(file))
    setFormRemoveImage(false)
  }

  const handleRemoveImage = () => {
    setFormImageFile(null)
    setFormPreview(null)
    setFormRemoveImage(true)
  }

  const handleSave = async () => {
    if (!formLabel.trim()) { setFormError('Label is required.'); return }
    setSaving(true)
    try {
      if (editTarget) {
        await api.updateServiceCategory(editTarget.id, {
          label: formLabel.trim(),
          order: formOrder ? parseInt(formOrder) : editTarget.order,
          isActive: formActive,
          image: formImageFile,
          removeImage: formRemoveImage,
        })
      } else {
        await api.createServiceCategory({
          label: formLabel.trim(),
          order: formOrder ? parseInt(formOrder) : total + 1,
          image: formImageFile,
        })
      }
      setShowModal(false)
      reload()
      toast(editTarget ? 'Service category updated' : 'Service category created')
    } catch (e: any) {
      setFormError(e.message ?? 'Failed to save.')
      toast(e?.message ?? 'Save failed', 'error')
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async () => {
    if (!deleteTarget) return
    try {
      await api.deleteServiceCategory(deleteTarget.id)
      setDeleteTarget(null)
      reload()
      toast('Service category deleted')
    } catch (e: any) {
      setActionError(e.message)
      toast(e?.message ?? 'Delete failed', 'error')
    }
  }

  const toggleStatus = async (category: ServiceCategory) => {
    try {
      await api.updateServiceCategory(category.id, {isActive: !category.isActive})
      reload()
      toast(category.isActive ? 'Service category disabled' : 'Service category activated')
    } catch (e: any) {
      setActionError(e.message)
      toast(e?.message ?? 'Status update failed', 'error')
    }
  }

  const inputStyle = {
    width: '100%', padding: '10px 14px', border: '1px solid #e2e8f0',
    borderRadius: 8, fontSize: 14, color: '#0f172a', background: '#f8fafc',
    outline: 'none', boxSizing: 'border-box' as const,
  }

  const labelStyle = {
    fontSize: 13, fontWeight: 600 as const, color: '#374151',
    display: 'block' as const, marginBottom: 6,
  }

  return (
    <div>
      {/* Header */}
      <div style={{marginBottom: 24}}>
        <h1 style={{fontSize: 24, fontWeight: 700, color: '#0f172a', margin: '0 0 4px'}}>Service Categories</h1>
        <p style={{fontSize: 14, color: '#64748b', margin: 0}}>Manage the service categories and cover images shown in the mobile app</p>
      </div>

      {/* Toolbar */}
      <div style={{
        background: '#fff', borderRadius: 12, border: '1px solid #e2e8f0',
        padding: '16px 20px', marginBottom: 16,
        boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
        display: 'flex', gap: 12, alignItems: 'center',
      }}>
        <div style={{position: 'relative', flex: 1}}>
          <svg style={{position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: '#94a3b8'}} width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2'>
            <circle cx='11' cy='11' r='8'/><line x1='21' y1='21' x2='16.65' y2='16.65'/>
          </svg>
          <input type='text' placeholder='Search categories...' value={search}
            onChange={e => setSearch(e.target.value)}
            style={{...inputStyle, paddingLeft: 38, background: '#fff'}} />
        </div>
        <button onClick={reload} disabled={loading}
          style={{display: 'flex', alignItems: 'center', gap: 6, padding: '10px 16px', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 14, fontWeight: 500, color: '#374151', background: '#f8fafc', cursor: 'pointer'}}>
          <svg width='14' height='14' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2' style={{animation: loading ? 'spin 1s linear infinite' : 'none'}}>
            <polyline points='23 4 23 10 17 10'/><polyline points='1 20 1 14 7 14'/>
            <path d='M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15'/>
          </svg>
          Refresh
        </button>
        <button onClick={openAdd}
          style={{display: 'flex', alignItems: 'center', gap: 6, padding: '10px 18px', border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 600, color: '#fff', background: '#3b82f6', cursor: 'pointer'}}>
          <svg width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2.5'><line x1='12' y1='5' x2='12' y2='19'/><line x1='5' y1='12' x2='19' y2='12'/></svg>
          Add Category
        </button>
      </div>

      {(error || actionError) && (
        <div style={{padding: '12px 16px', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, color: '#dc2626', fontSize: 14, marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
          <span>{error || actionError}</span>
          <button onClick={() => { setActionError(''); reload() }} style={{color: '#3b82f6', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 500, fontSize: 13}}>Retry</button>
        </div>
      )}

      {/* Table */}
      <div style={{background: '#fff', borderRadius: 12, border: '1px solid #e2e8f0', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', overflow: 'hidden'}}>
        {loading ? (
          <div style={{display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '60px 0'}}>
            <div style={{width: 32, height: 32, border: '3px solid #e2e8f0', borderTopColor: '#3b82f6', borderRadius: '50%', animation: 'spin 0.8s linear infinite'}} />
          </div>
        ) : (
          <div style={{overflowX: 'auto'}}>
            <table style={{width: '100%', borderCollapse: 'collapse', fontSize: 14}}>
              <thead>
                <tr style={{background: '#f8fafc', borderBottom: '1px solid #e2e8f0'}}>
                  {['ORDER', 'IMAGE', 'CATEGORY', 'STATUS', 'ACTIONS'].map(h => (
                    <th key={h} style={{padding: '11px 20px', textAlign: 'left', fontSize: 11, fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.06em', whiteSpace: 'nowrap'}}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {categories.map((item, i) => (
                  <tr key={item.id}
                    style={{borderBottom: i < categories.length - 1 ? '1px solid #f1f5f9' : 'none'}}
                    onMouseEnter={ev => (ev.currentTarget.style.background = '#fafafa')}
                    onMouseLeave={ev => (ev.currentTarget.style.background = 'transparent')}
                  >
                    {/* Order */}
                    <td style={{padding: '14px 20px', width: 80}}>
                      <span style={{fontSize: 13, fontWeight: 600, color: '#94a3b8', background: '#f1f5f9', padding: '3px 10px', borderRadius: 20}}>
                        #{item.order}
                      </span>
                    </td>

                    {/* Image */}
                    <td style={{padding: '14px 20px', width: 90}}>
                      {item.image ? (
                        <img src={item.image} alt={item.label}
                          style={{width: 56, height: 56, borderRadius: 10, objectFit: 'cover', border: '1px solid #e2e8f0'}} />
                      ) : (
                        <div style={{
                          width: 56, height: 56, borderRadius: 10, background: '#f1f5f9',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          color: '#cbd5e1',
                        }}>
                          <svg width='22' height='22' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2'>
                            <rect x='3' y='3' width='18' height='18' rx='2' ry='2'/><circle cx='8.5' cy='8.5' r='1.5'/><polyline points='21 15 16 10 5 21'/>
                          </svg>
                        </div>
                      )}
                    </td>

                    {/* Category */}
                    <td style={{padding: '14px 20px', minWidth: 180}}>
                      <span style={{fontWeight: 700, color: '#0f172a', fontSize: 14}}>{item.label}</span>
                      {!item.image && (
                        <span style={{display: 'block', fontSize: 11, color: '#94a3b8', marginTop: 3}}>No image — mobile shows fallback icon</span>
                      )}
                    </td>

                    {/* Status */}
                    <td style={{padding: '14px 20px'}}>
                      <div style={{display: 'flex', alignItems: 'center', gap: 10}}>
                        <Toggle checked={item.isActive} onChange={() => toggleStatus(item)} />
                        <span style={{fontSize: 13, fontWeight: 500, color: item.isActive ? '#16a34a' : '#94a3b8'}}>
                          {item.isActive ? 'Active' : 'Disabled'}
                        </span>
                      </div>
                    </td>

                    {/* Actions */}
                    <td style={{padding: '14px 20px'}}>
                      <div style={{display: 'flex', gap: 8}}>
                        <button onClick={() => openEdit(item)}
                          style={{padding: '6px 14px', border: '1px solid #e2e8f0', borderRadius: 7, fontSize: 13, fontWeight: 500, color: '#374151', background: '#fff', cursor: 'pointer'}}
                          onMouseEnter={e => { (e.currentTarget as HTMLElement).style.background = '#f8fafc' }}
                          onMouseLeave={e => { (e.currentTarget as HTMLElement).style.background = '#fff' }}>
                          Edit
                        </button>
                        <button onClick={() => setDeleteTarget(item)}
                          style={{padding: '6px 14px', border: '1px solid #fecaca', borderRadius: 7, fontSize: 13, fontWeight: 500, color: '#ef4444', background: '#fff', cursor: 'pointer'}}
                          onMouseEnter={e => { (e.currentTarget as HTMLElement).style.background = '#fef2f2' }}
                          onMouseLeave={e => { (e.currentTarget as HTMLElement).style.background = '#fff' }}>
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
                {categories.length === 0 && (
                  <tr><td colSpan={5} style={{padding: '48px 20px', textAlign: 'center', color: '#94a3b8'}}>No categories found</td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {!loading && (
          <div style={{padding: '12px 20px', borderTop: '1px solid #f1f5f9', display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
            <span style={{fontSize: 13, color: '#94a3b8'}}>Showing {categories.length} of {total} categories</span>
            <div style={{display: 'flex', gap: 8}}>
              <span style={{padding: '3px 10px', borderRadius: 20, fontSize: 12, fontWeight: 500, background: '#dcfce7', color: '#16a34a'}}>
                Active: {categories.filter(c => c.isActive).length}
              </span>
              <span style={{padding: '3px 10px', borderRadius: 20, fontSize: 12, fontWeight: 500, background: '#fef3c7', color: '#b45309'}}>
                Disabled: {categories.filter(c => !c.isActive).length}
              </span>
              <span style={{padding: '3px 10px', borderRadius: 20, fontSize: 12, fontWeight: 500, background: '#f1f5f9', color: '#64748b'}}>
                Icon fallback: {categories.filter(c => !c.image).length}
              </span>
            </div>
          </div>
        )}
      </div>

      <Pagination page={page} limit={limit} total={total} onPageChange={setPage} />

      {/* Add / Edit Modal */}
      {showModal && (
        <div onClick={() => setShowModal(false)}
          style={{position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.45)', zIndex: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20}}>
          <div onClick={e => e.stopPropagation()}
            style={{background: '#fff', borderRadius: 16, width: 500, boxShadow: '0 24px 64px rgba(0,0,0,0.15)', overflow: 'hidden'}}>
            {/* Header */}
            <div style={{padding: '20px 24px', borderBottom: '1px solid #f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'space-between'}}>
              <h3 style={{fontSize: 18, fontWeight: 700, color: '#0f172a', margin: 0}}>
                {editTarget ? 'Edit Category' : 'Add New Category'}
              </h3>
              <button onClick={() => setShowModal(false)}
                style={{background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8', padding: 4}}>
                <svg width='20' height='20' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2'><line x1='18' y1='6' x2='6' y2='18'/><line x1='6' y1='6' x2='18' y2='18'/></svg>
              </button>
            </div>

            {/* Body */}
            <div style={{padding: '24px', display: 'flex', flexDirection: 'column', gap: 18}}>
              {formError && (
                <div style={{padding: '10px 14px', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, color: '#dc2626', fontSize: 13}}>
                  {formError}
                </div>
              )}

              <div>
                <label style={labelStyle}>Label <span style={{color: '#ef4444'}}>*</span></label>
                <input placeholder='e.g. Photography' value={formLabel} onChange={e => setFormLabel(e.target.value)} style={inputStyle} />
              </div>

              <div>
                <label style={labelStyle}>Cover Image</label>
                <ImagePicker preview={formPreview} onSelect={handleSelectImage} onRemove={handleRemoveImage} />
                <div style={{fontSize: 11, color: '#94a3b8', marginTop: 6}}>If left empty, the mobile app shows a fallback icon. Max 5MB.</div>
              </div>

              <div>
                <label style={labelStyle}>Display Order</label>
                <input type='number' placeholder='e.g. 1' value={formOrder}
                  onChange={e => setFormOrder(e.target.value)} style={inputStyle} />
              </div>

              {editTarget && (
                <div>
                  <label style={labelStyle}>Status</label>
                  <div style={{display: 'flex', alignItems: 'center', gap: 10}}>
                    <Toggle checked={formActive} onChange={() => setFormActive(a => !a)} />
                    <span style={{fontSize: 14, fontWeight: 500, color: formActive ? '#16a34a' : '#94a3b8'}}>
                      {formActive ? 'Active' : 'Disabled'}
                    </span>
                  </div>
                  <div style={{fontSize: 11, color: '#94a3b8', marginTop: 6}}>Disabled categories are hidden from the mobile app.</div>
                </div>
              )}
            </div>

            {/* Footer */}
            <div style={{padding: '16px 24px', borderTop: '1px solid #f1f5f9', display: 'flex', justifyContent: 'flex-end', gap: 10}}>
              <button onClick={() => setShowModal(false)} disabled={saving}
                style={{padding: '9px 20px', borderRadius: 8, border: '1px solid #e2e8f0', background: '#fff', fontSize: 14, cursor: 'pointer', color: '#374151'}}>
                Cancel
              </button>
              <button onClick={handleSave} disabled={saving}
                style={{padding: '9px 20px', borderRadius: 8, border: 'none', background: '#3b82f6', fontSize: 14, fontWeight: 600, cursor: 'pointer', color: '#fff', opacity: saving ? 0.7 : 1}}>
                {saving ? 'Saving...' : editTarget ? 'Save Changes' : 'Add Category'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirm */}
      {deleteTarget && (
        <div onClick={() => setDeleteTarget(null)}
          style={{position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.45)', zIndex: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20}}>
          <div onClick={e => e.stopPropagation()}
            style={{background: '#fff', borderRadius: 16, width: 420, boxShadow: '0 24px 64px rgba(0,0,0,0.15)', padding: '32px 28px', textAlign: 'center'}}>
            <div style={{width: 56, height: 56, borderRadius: '50%', background: '#fef2f2', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px'}}>
              <svg width='24' height='24' viewBox='0 0 24 24' fill='none' stroke='#ef4444' strokeWidth='2'>
                <polyline points='3 6 5 6 21 6'/><path d='M19 6l-1 14H6L5 6'/>
                <path d='M10 11v6M14 11v6'/><path d='M9 6V4h6v2'/>
              </svg>
            </div>
            <h3 style={{fontSize: 18, fontWeight: 700, color: '#0f172a', margin: '0 0 8px'}}>Delete Category</h3>
            <p style={{fontSize: 14, color: '#64748b', margin: '0 0 24px', lineHeight: 1.6}}>
              Are you sure you want to delete <strong style={{color: '#0f172a'}}>{deleteTarget.label}</strong>?
              This will remove it from the platform.
            </p>
            <div style={{display: 'flex', gap: 10, justifyContent: 'center'}}>
              <button onClick={() => setDeleteTarget(null)}
                style={{padding: '9px 24px', borderRadius: 8, border: '1px solid #e2e8f0', background: '#fff', fontSize: 14, cursor: 'pointer', color: '#374151'}}>
                Cancel
              </button>
              <button onClick={handleDelete}
                style={{padding: '9px 24px', borderRadius: 8, border: 'none', background: '#ef4444', fontSize: 14, fontWeight: 600, cursor: 'pointer', color: '#fff'}}>
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  )
}

const ServiceCategoriesPageWrapper: FC = () => <ServiceCategoriesPage />

export {ServiceCategoriesPageWrapper}
